import React, { useState, useEffect } from "react";
import { FaCamera, FaEye, FaEyeSlash, FaUserCircle } from "react-icons/fa";
import axiosInstance from "../api/axiosConfig"; // Adjust import path as needed
import PageHeader from "../components/PageHeader";
import notify from "../utils/toast";

const Profile = () => {
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "",
    simple_pass: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Get admin ID from localStorage
  const getAdminId = () => {
    try {
      const adminData = localStorage.getItem('admin');
      if (adminData) {
        const admin = JSON.parse(adminData);
        return admin.id || admin._id;
      }
      return null;
    } catch (error) {
      console.error("Error parsing admin data:", error);
      return null;
    }
  };

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const adminId = getAdminId();
      
      if (!adminId) {
        setError("Admin ID not found in localStorage");
        setLoading(false);
        return;
      }

      console.log("Fetching profile for admin ID:", adminId);
      
      // Call API with adminId as query parameter
      const response = await ProfileApi(adminId);
      console.log("Profile response:", response);
      
      const { data } = response;
      
      // Split name into first and last name
      const nameParts = data.name ? data.name.split(" ") : ["", ""];
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      setFormData({
        firstName: firstName,
        lastName: lastName,
        email: data.email || "",
        phone: data.phone || "",
        role: data.role || "",
        simple_pass: data.simple_pass || "",
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
      setError(error.response?.data?.message || "Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  const handleImage = (e) => {
    const file = e.target.files[0];

    if (file) {
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prepare data for API update
    const updateData = {
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      phone: formData.phone,
      role: formData.role,
    };
    
    try {
      // Call update API here
      // await updateProfileApi(updateData);
      console.log("Update data:", updateData);
      // Show success message
      notify.success("Your profile has been updated.", "Profile saved");
    } catch (error) {
      console.error("Error updating profile:", error);
      notify.error(error, "Could not update your profile");
    }
  };

  const handleCancel = () => {
    // Reset to original data
    fetchProfile();
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (loading) {
    return (
      <div className="profile-page lead-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page lead-page">
        <div className="alert alert-danger">
          <h5>Error</h5>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchProfile}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page lead-page">
      <PageHeader
        title="Profile Settings"
        subtitle="View your profile information"
      />

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Basic Info */}
            <div className="border-bottom mb-4 pb-4">
              <h5 className="mb-3">Personal Information</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    className="form-control"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    className="form-control"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder="Enter email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled // Email usually can't be changed
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Role</label>
                  <input
                    type="text"
                    name="role"
                    className="form-control"
                    placeholder="Enter role"
                    value={formData.role}
                    disabled
                  />
                </div>

                {formData.simple_pass && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Simple Password</label>
                    <div className="input-group">
                      <input
                        type={showPassword.current ? "text" : "password"}
                        className="form-control"
                        value={formData.simple_pass}
                        disabled
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility('current')}
                      >
                        {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                Update Profile
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleCancel}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Profile Info Card */}
      <div className="card border-0 shadow-sm mt-4">
        <div className="card-body">
          <h5 className="mb-3">Account Information</h5>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-2">
                <strong>Admin ID:</strong>{" "}
                <span className="text-muted">{getAdminId()}</span>
              </div>
              <div className="mb-2">
                <strong>Role:</strong>{" "}
                <span className="badge bg-primary">{formData.role}</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-2">
                <strong>Full Name:</strong>{" "}
                <span className="text-muted">
                  {formData.firstName} {formData.lastName}
                </span>
              </div>
              <div className="mb-2">
                <strong>Email:</strong>{" "}
                <span className="text-muted">{formData.email}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

// API function with adminId parameter
export const ProfileApi = async (adminId) => {
  // Send adminId as query parameter
  const response = await axiosInstance.get(`/admin-auth/profile?adminId=${adminId}`);
  return response.data;
};

// Update profile API (if needed)
export const updateProfileApi = async (data) => {
  const response = await axiosInstance.put("/admin-auth/profile", data);
  return response.data;
};